import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Calendar, Tag, FileText, Loader2, CheckCircle2, ChevronRight, ChevronLeft, Building2, Plus, Info } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { mesesParaEscolher, periodoParaGravar, textoPeriodo, type AteQuando } from '../../../src/lib/avisosUc';
import SeletorAteQuando from './avisos/SeletorAteQuando';

export interface UcDoAviso {
    id: number | string;
    numero_uc?: string | null;
    outro_numero_uc?: string | null;
    nome_cliente?: string | null;
}

interface AgendarLembreteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    /** Aberto pela aba "Avisos" da UC: a UC já vem escolhida e o passo 1 é pulado. */
    ucInicial?: UcDoAviso | null;
}

type Tipo = 'acrescimo' | 'desconto' | 'outro';

const Dica: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex gap-2 p-3 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/40 text-xs text-sky-900 dark:text-sky-200 leading-relaxed">
        <Info size={15} className="shrink-0 mt-0.5" />
        <div>{children}</div>
    </div>
);

// Um aviso por UC, com início e "até quando" (src/lib/avisosUc.ts). Até 04/11/2026 cada
// mês marcado virava uma cópia, e o aviso de "todo mês" acabava quando os 12 meses
// oferecidos acabavam.
const AgendarLembreteModal: React.FC<AgendarLembreteModalProps> = ({ isOpen, onClose, onSuccess, ucInicial }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const primeiroPasso = ucInicial ? 2 : 1;
    const [step, setStep] = useState(primeiroPasso);
    const [loading, setLoading] = useState(false);

    // Passo 1: UC
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<UcDoAviso[]>([]);
    const [searching, setSearching] = useState(false);
    const [selectedUcs, setSelectedUcs] = useState<UcDoAviso[]>(ucInicial ? [ucInicial] : []);

    // Passo 2: quando
    const meses = useMemo(() => mesesParaEscolher(), [isOpen]);
    const [inicio, setInicio] = useState(meses[0]);
    const [ate, setAte] = useState<AteQuando>({ tipo: 'sem_prazo' });

    // Passo 3: tipo
    const [tipoLembrete, setTipoLembrete] = useState<Tipo>('outro');

    // Passo 4: descrição
    const [descricao, setDescricao] = useState('');

    useEffect(() => {
        if (isOpen) {
            setStep(ucInicial ? 2 : 1);
            setSelectedUcs(ucInicial ? [ucInicial] : []);
            setSearchTerm('');
            setSearchResults([]);
            setInicio(meses[0]);
            setAte({ tipo: 'sem_prazo' });
            setTipoLembrete('outro');
            setDescricao('');
        }
        // Só ao abrir ou trocar de UC: o objeto da UC muda a cada render da tela de trás,
        // e depender dele zeraria o formulário no meio da digitação.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, ucInicial?.id]);

    useEffect(() => {
        const fetchUcs = async () => {
            // Vírgula e parênteses quebram o filtro "or" do PostgREST.
            const termo = searchTerm.replace(/[,()"\\]/g, ' ').trim();
            if (termo.length < 3) {
                setSearchResults([]);
                return;
            }
            setSearching(true);
            try {
                // Número com ou sem pontos acha a UC cadastrada do outro jeito (#6599).
                const digitos = termo.replace(/\D/g, '').replace(/^0+/, '');
                const filtros = [`nome_cliente.ilike.%${termo}%`, `numero_uc.ilike.%${termo}%`, `outro_numero_uc.ilike.%${termo}%`];
                if (digitos.length >= 3) filtros.push(`numero_uc_norm.like.%${digitos}%`, `outro_numero_uc_norm.like.%${digitos}%`);
                const { data, error } = await supabase
                    .from('unidades_consumidoras')
                    .select('id, numero_uc, outro_numero_uc, nome_cliente')
                    .or(filtros.join(','))
                    .limit(10);
                if (error) throw error;
                setSearchResults(data || []);
            } catch (err) {
                console.error(err);
            } finally {
                setSearching(false);
            }
        };

        const timeoutId = setTimeout(fetchUcs, 500);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const periodo = periodoParaGravar(inicio, ate);
    const erroPeriodo = typeof periodo === 'string' ? periodo : null;
    const textoDoPeriodo = typeof periodo === 'string' ? '' : textoPeriodo({ id: '', tipo: tipoLembrete, descricao: '', status: 'pendente', mes_inicio: periodo.mes_inicio, mes_fim: periodo.mes_fim });

    const handleSave = async () => {
        if (selectedUcs.length === 0) return addToast('Selecione pelo menos uma UC.', 'error');
        if (typeof periodo === 'string') return addToast(periodo, 'error');
        if (!descricao.trim()) return addToast('Digite a descrição.', 'error');

        setLoading(true);
        try {
            const criadoPorNome = profile?.full_name || user?.user_metadata?.full_name || user?.email || 'Usuário Desconhecido';
            const payload = selectedUcs.map(uc => ({
                uc_id: uc.id,
                uc_codigo: uc.numero_uc || uc.outro_numero_uc,
                nome_cliente: uc.nome_cliente,
                tipo: tipoLembrete,
                ...periodo,
                descricao: descricao.trim(),
                status: 'pendente',
                criado_por_id: user?.id || null,
                criado_por_nome: criadoPorNome,
            }));

            const { error } = await supabase.from('lembretes_faturamento').insert(payload);
            if (error) throw error;
            addToast(payload.length === 1 ? 'Aviso criado.' : `${payload.length} avisos criados (um por UC).`, 'success');
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'agendar_lembrete', origem: 'acao' });
            onSuccess?.();
            onClose();
        } catch (err: any) {
            console.error(err);
            addToast(err.message || 'Erro ao agendar o aviso.', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const passos = [1, 2, 3, 4].filter(i => i >= primeiroPasso);
    const renderStepIndicators = () => (
        <div className="flex items-center justify-center gap-2 mb-8">
            {passos.map((i, k) => (
                <div key={i} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                        ${step === i ? 'bg-primary text-white' :
                            step > i ? 'bg-primary/20 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>
                        {step > i ? <CheckCircle2 size={16} /> : k + 1}
                    </div>
                    {k < passos.length - 1 && (
                        <div className={`w-12 h-1 rounded-full transition-colors ${step > i ? 'bg-primary/40' : 'bg-gray-100 dark:bg-gray-700'}`} />
                    )}
                </div>
            ))}
        </div>
    );

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                            <Calendar className="text-primary" />
                            {ucInicial ? 'Novo aviso' : 'Agendar Lembrete'}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {ucInicial
                                ? `${ucInicial.nome_cliente || 'UC'} · UC ${ucInicial.numero_uc || ucInicial.outro_numero_uc || ''}`
                                : 'Aparece para quem valida, calcula e emite a fatura'}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {renderStepIndicators()}

                    {/* PASSO 1: UC */}
                    {step === 1 && (
                        <div className="space-y-4 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Para quais clientes é o aviso?</h3>

                            {selectedUcs.length > 0 && (
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1.5 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-800">
                                    {selectedUcs.map(uc => (
                                        <div key={uc.id} className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-primary pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium animate-scale-in">
                                            <span className="truncate max-w-[150px] font-semibold">{uc.nome_cliente}</span>
                                            <span className="opacity-75 truncate max-w-[80px] font-normal">(UC: {uc.numero_uc || uc.outro_numero_uc})</span>
                                            <button
                                                onClick={() => setSelectedUcs(prev => prev.filter(x => x.id !== uc.id))}
                                                className="p-0.5 hover:bg-primary/20 rounded-full transition-colors text-primary"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="search"
                                    placeholder="Buscar por nome, UC..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all dark:text-white"
                                />
                                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" size={18} />}
                            </div>

                            {searchResults.length > 0 ? (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 max-h-60 overflow-y-auto bg-white dark:bg-gray-800 shadow-sm">
                                    {searchResults.map(uc => {
                                        const isSelected = selectedUcs.some(x => x.id === uc.id);
                                        return (
                                            <button
                                                key={uc.id}
                                                onClick={() => setSelectedUcs(prev => isSelected ? prev.filter(x => x.id !== uc.id) : [...prev, uc])}
                                                className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex flex-col border-l-2 ${isSelected ? 'bg-primary/5 border-l-primary' : 'border-l-transparent'}`}
                                            >
                                                <div className="flex items-center justify-between w-full">
                                                    <span className="font-medium text-gray-800 dark:text-white truncate">{uc.nome_cliente}</span>
                                                    {isSelected && <CheckCircle2 size={16} className="text-primary shrink-0 ml-2" />}
                                                </div>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">UC: {uc.numero_uc || uc.outro_numero_uc}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : searchTerm.length >= 3 && !searching ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">Nenhuma UC encontrada.</p>
                            ) : null}
                        </div>
                    )}

                    {/* PASSO 2: quando */}
                    {step === 2 && (
                        <div className="space-y-5 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Para quais faturas vale?</h3>
                            <div className="space-y-1">
                                <label htmlFor="aviso-inicio" className="text-sm font-medium text-gray-700 dark:text-gray-300">A partir da fatura de</label>
                                <select
                                    id="aviso-inicio"
                                    value={inicio}
                                    onChange={e => {
                                        const novo = e.target.value;
                                        setInicio(novo);
                                        // "Até" antes do novo início não fecha: passa a ser o próprio início.
                                        if (ate.tipo === 'ate' && meses.indexOf(ate.mes) < meses.indexOf(novo)) setAte({ tipo: 'ate', mes: novo });
                                    }}
                                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 outline-none"
                                >
                                    {meses.map((m, k) => <option key={m} value={m}>{m}{k === 0 ? ' (a que está sendo faturada agora)' : ''}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Até quando?</span>
                                <SeletorAteQuando meses={meses} inicio={inicio} valor={ate} onChange={setAte} />
                            </div>
                            {erroPeriodo && <p className="text-xs text-red-600">{erroPeriodo}</p>}
                        </div>
                    )}

                    {/* PASSO 3: tipo */}
                    {step === 3 && (
                        <div className="space-y-4 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Que tipo de aviso?</h3>
                            <div className="grid grid-cols-1 gap-3">
                                {[
                                    { id: 'desconto', label: 'Desconto', icon: Tag, desc: 'Desconto diferente em uma ou algumas faturas' },
                                    { id: 'acrescimo', label: 'Acréscimo', icon: Plus, desc: 'Cobrar algo a mais na fatura' },
                                    { id: 'outro', label: 'Outro', icon: FileText, desc: 'Instrução para quem calcula ou emite' }
                                ].map((t) => {
                                    const Icon = t.icon;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setTipoLembrete(t.id as Tipo)}
                                            className={`flex items-center gap-4 p-4 border rounded-xl transition-all text-left
                                                ${tipoLembrete === t.id
                                                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                        >
                                            <div className={`p-2 rounded-lg ${tipoLembrete === t.id ? 'bg-primary/20 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                                <Icon size={20} />
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800 dark:text-white">{t.label}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{t.desc}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {tipoLembrete === 'desconto' ? (
                                <Dica>
                                    <strong>O desconto mudou de vez?</strong> Mude no cadastro da UC (aba Dados Cadastrais). O cálculo já
                                    preenche o desconto do cadastro em toda fatura, sem depender de aviso. Use o aviso para desconto de uma ou
                                    algumas faturas (indicação, campanha, acerto).
                                </Dica>
                            ) : (
                                <Dica>
                                    <strong>Dia de vencimento fixo</strong> e <strong>nome/CNPJ no boleto</strong> ficam no cadastro da UC
                                    (aba Dados Cadastrais: “Vencimento do boleto” e “Pagador do boleto”). O sistema aplica sozinho em toda fatura.
                                </Dica>
                            )}
                        </div>
                    )}

                    {/* PASSO 4: descrição */}
                    {step === 4 && (
                        <div className="space-y-4 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Descreva o aviso</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Escreva o que quem calcula a fatura precisa fazer.</p>

                            <textarea
                                value={descricao}
                                onChange={(e) => setDescricao(e.target.value)}
                                rows={4}
                                placeholder="Ex: Adicionar R$ 50,00 de desconto ref. a indicação."
                                className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none"
                            />

                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700 space-y-2 mt-4">
                                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Resumo</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div className="text-gray-500 flex items-center gap-1"><Building2 size={14} /> UCs:</div>
                                    <div className="font-bold text-gray-900 dark:text-white text-right">{selectedUcs.length} UC(s) · um aviso por UC</div>

                                    <div className="text-gray-500 flex items-center gap-1"><Calendar size={14} /> Vale:</div>
                                    <div className="font-bold text-gray-900 dark:text-white text-right">{textoDoPeriodo}</div>

                                    <div className="text-gray-500 flex items-center gap-1"><Tag size={14} /> Tipo:</div>
                                    <div className="font-medium text-gray-900 dark:text-white text-right capitalize">{tipoLembrete === 'acrescimo' ? 'Acréscimo' : tipoLembrete}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0 bg-gray-50 dark:bg-gray-800/50">
                    {step > primeiroPasso ? (
                        <button
                            onClick={() => setStep(step - 1)}
                            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium flex items-center gap-1 transition-colors"
                        >
                            <ChevronLeft size={18} /> Voltar
                        </button>
                    ) : <div></div>}

                    {step < 4 ? (
                        <button
                            onClick={() => setStep(step + 1)}
                            disabled={(step === 1 && selectedUcs.length === 0) || (step === 2 && !!erroPeriodo)}
                            className="px-6 py-2 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Próximo <ChevronRight size={18} />
                        </button>
                    ) : (
                        <button
                            onClick={handleSave}
                            disabled={loading || !descricao.trim()}
                            className="px-6 py-2 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                        >
                            {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                            Criar aviso
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AgendarLembreteModal;
